import { useEffect } from 'react';
import type { FC } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Drawer, Hidden } from '@mui/material';
import Scrollbar from '../Scrollbar';
import NavSection from '../NavSection';
import { Logo } from '../Logo';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import AddCircleIcon from '@mui/icons-material/AddCircle';

interface ProfileSidebarProps {
    onMobileClose: () => void;
    openMobile: boolean;
    isReseller: boolean;
}

const clientSections = [
    {
        title: 'Client Profile',
        items: [
            {
                title: 'Orders',
                path: '/client/orders',
                icon: <ReceiptIcon fontSize="small" />,
            },
            {
                title: 'Profile',
                path: '/client/profile',
                icon: <AccountCircleIcon fontSize="small" />,
            },
            {
                title: 'Upgrades',
                path: '/client/upgrades',
                icon: <UpgradeIcon fontSize="small" />,
            },
            {
                title: 'Discounts',
                path: '/client/discounts',
                icon: <LocalOfferIcon fontSize="small" />,
            },
            {
                title: 'Support',
                path: '/client/support',
                icon: <HelpCenterIcon fontSize="small" />,
            },
        ],
    },
];

const resellerSections = [
    {
        title: 'Reseller Profile',
        items: [
            {
                title: 'New Order',
                path: '/reseller/orders/new',
                icon: <AddCircleIcon fontSize="small" />,
            },
            {
                title: 'Orders',
                path: '/reseller/orders',
                icon: <ReceiptIcon fontSize="small" />,
            },
            {
                title: 'Profile',
                path: '/reseller/profile',
                icon: <AccountCircleIcon fontSize="small" />,
            },
            {
                title: 'Support',
                path: '/reseller/support',
                icon: <HelpCenterIcon fontSize="small" />,
            },
        ],
    },
];

export const ProfileSidebar: FC<ProfileSidebarProps> = (props) => {
    const { onMobileClose, openMobile, isReseller } = props;
    const location = useLocation();
    const sections = isReseller ? resellerSections : clientSections;

    useEffect(() => {
        if (openMobile && onMobileClose) {
            onMobileClose();
        }
    }, [location.pathname]);

    const content = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
        >
            <Scrollbar options={{ suppressScrollX: true }}>
                <Hidden lgUp>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            p: 2,
                        }}
                    >
                        <RouterLink to="/">
                            <Logo />
                        </RouterLink>
                    </Box>
                </Hidden>
                <Box sx={{ p: 2 }}>
                    {sections.map((section) => (
                        <NavSection
                            key={section.title}
                            pathname={location.pathname}
                            sx={{
                                '& + &': {
                                    mt: 3,
                                },
                            }}
                            {...section}
                        />
                    ))}
                </Box>
            </Scrollbar>
        </Box>
    );

    return (
        <>
            <Drawer
                anchor="left"
                onClose={onMobileClose}
                open={openMobile}
                PaperProps={{
                    sx: {
                        backgroundColor: 'background.paper',
                        width: 280,
                        display: { xs: 'block', lg: 'none' },
                    },
                }}
                variant="temporary"
            >
                {content}
            </Drawer>
            <Drawer
                anchor="left"
                open
                PaperProps={{
                    sx: {
                        backgroundColor: 'background.paper',
                        height: 'calc(100% - 64px) !important',
                        top: '64px !Important',
                        width: 280,
                        display: { xs: 'none', lg: 'block' },
                    },
                }}
                variant="persistent"
            >
                {content}
            </Drawer>
        </>
    );
};
